import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import {
  BasicColors,
  CheckoutBrandingModel,
  CheckoutOptionsPageModel,
  CheckoutPaymentPageModel,
  CustomColors,
  CustomerFacingCheckOutItemModel,
  CustomerFacingCheckOutModel,
} from "../../../shared/models";
import {
  CustomLabel,
  FlexContainer,
  Loader,
} from "@mindmemobile/mm-shared-global";
import { ICheckOutCombinedColors } from "../../../shared/interfaces";
import {
  CheckoutColorType,
  CheckOutDisplayTypes,
  CheckOutPayButtonCaption,
  IFRAMEMessageTypes,
  UpsertCheckoutActiveStep,
  UpsertPurchaseActiveStep,
} from "../../../shared/enums";
import { PurchaseStore } from "../../../shared/store";
import {
  DonationItem,
  MultipleItems,
  PurchaseSection,
  PurchaseTopBar,
  SingleItemMultiplePrices,
} from "../../../shared/components";
import {
  PurchaseColoredBackground,
  PurchaseWrapper,
} from "../../../shared/styles";
import styled from "styled-components";

const DetailPagePreviewWrapper = styled.div`
  &.donation-only {
    .purchase-section {
      padding: 0px !important;
    }
    .donation-img-wrapper {
      gap: 0px !important;
    }
    .donation-left-right-image {
      height: calc(100vh - 131px);
    }
    .donation-ad-block {
      max-height: calc(100vh - 177px);
    }
    .purchase-middle-panel {
      /* overflow-y:unset; */
      > div {
        padding: 0px;
      }
    }
  }
  .donation-img-bg-wrapper {
    height: calc(100vh - 131px);
    padding: 0px !important;
  }
  .donation-left-right-image {
    /* max-width: 460px; */
    height: calc(100vh - 62px);
    width: 50%;
    .donation-carousel-img {
      display: inline-block;
      // cover is creating issue for the images with text
      // this object fill will make change in detail page preview
      object-fit: cover;  
      &.carousel-original {
        object-fit: contain;
        max-width: 100%;
        max-height: 100%;
        height: auto;
        width: auto;
        border-radius: 10px;
      }
    }
    &.carousel-fit-left {
      display: flex;
      justify-content: center;
      align-items: center;
      padding-left: 50px;
    }
    &.carousel-fit-right {
      display: flex;
      justify-content: center;
      align-items: center;
      padding-right: 50px;
    }
  }
  .nav-flex-no-height {
    .donation-item-wrapper .donation-img-wrapper .donation-left-right-image {
      height: calc(100vh - 89px);
    }
  }
  @media (max-width: 768px) {
    .donation-img-bg-wrapper {
      height: calc(100vh - 141px);
    }
  }
`;
const CheckoutPreviewForDetailPage = () => {
  const [customerFacingCheckOutDetails, setCustomerFacingCheckoutDetails] =
    useState<CustomerFacingCheckOutModel | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    const handleMessage = (event: any) => {
      try {
        if (!event?.data || typeof event?.data !== "string") return;

        const data = JSON.parse(event.data);
        if (data?.type !== IFRAMEMessageTypes.CheckOutDetailPreivewData) return;

        const { customerFacingCheckOutDetails } = data;
        if (!customerFacingCheckOutDetails) return;
        const newDetails = new CustomerFacingCheckOutModel({
          ...customerFacingCheckOutDetails,
          items: customerFacingCheckOutDetails.items.map(
            (item: CustomerFacingCheckOutItemModel) => {
              return new CustomerFacingCheckOutItemModel({ ...item });
            }
          ),
          options: new CheckoutOptionsPageModel({
            ...customerFacingCheckOutDetails.options,
            brandingData: new CheckoutBrandingModel({
              ...customerFacingCheckOutDetails.options.brandingData,
              customColors: new CustomColors({
                ...customerFacingCheckOutDetails.options.brandingData
                  .customColors,
              }),
              basicColors: new BasicColors({
                ...customerFacingCheckOutDetails.options.brandingData
                  .basicColors,
              }),
            }),
          }),
          paymentDetails: new CheckoutPaymentPageModel({
            ...data.paymentDetails,
          }),
        });
        setCustomerFacingCheckoutDetails(newDetails);
        window.parent.postMessage(
          JSON.stringify({
            type: IFRAMEMessageTypes.PreviewMessageDataReceived,
          }),
          "*"
        );
        setLoading(false);
      } catch (error) {
        console.error("Failed to parse data:", error);
      }
    };

    window.removeEventListener("message", handleMessage);
    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  const getColors = () => {
    if (!customerFacingCheckOutDetails) return null;
    const brandingData = customerFacingCheckOutDetails.options.brandingData;
    if (!brandingData) return null;

    switch (brandingData.selectedColorType) {
      case CheckoutColorType.BasicColor:
        return brandingData.basicColors.getBasicColors() as ICheckOutCombinedColors;
      case CheckoutColorType.CustomColor:
        return brandingData.customColors.getCustomColors() as ICheckOutCombinedColors;
    }
  };

  const getBrandingData = () => {
    if (!customerFacingCheckOutDetails) return null;
    if (!customerFacingCheckOutDetails.options.brandingData) return null;
    return customerFacingCheckOutDetails.options.brandingData;
  };

  const getButtonCaption = () => {
    const brandingData = getBrandingData();
    if (!brandingData) return null;

    const { selectedColorType, basicColors, customColors } = brandingData;

    if (selectedColorType === CheckoutColorType.BasicColor) {
      return basicColors.payButtonType === CheckOutPayButtonCaption.Custom
        ? basicColors.buttonCustomCaption
        : CheckOutPayButtonCaption[basicColors.payButtonType];
    } else if (selectedColorType === CheckoutColorType.CustomColor) {
      return customColors.payButtonType === CheckOutPayButtonCaption.Custom
        ? customColors.buttonCustomCaption
        : CheckOutPayButtonCaption[customColors.payButtonType];
    }
  };

  const getButtonStyle = () => {
    const brandingData = getBrandingData();
    if (!brandingData) return null;

    const { selectedColorType, basicColors, customColors } = brandingData;

    if (selectedColorType === CheckoutColorType.BasicColor) {
      return basicColors.buttonStyleType;
    } else if (selectedColorType === CheckoutColorType.CustomColor) {
      return customColors.buttonStyleType;
    }
  };

  const getSellPage = () => {
    if (!customerFacingCheckOutDetails) return null;
    const colors = getColors();
    const displayType = customerFacingCheckOutDetails?.displayType;
    let content;

    switch (displayType) {
      case CheckOutDisplayTypes.MultipleItemsInbundle:
      case CheckOutDisplayTypes.SingleItem:
        content = (
          <PurchaseSection
            item={customerFacingCheckOutDetails?.items[0]}
            purchaseStore={new PurchaseStore()}
            onPurchaseClick={() => {}}
            colors={colors}
            buttonCaption={getButtonCaption()}
            buttonStyle={getButtonStyle()}
          />
        );
        break;
      case CheckOutDisplayTypes.SingleItemWithMultiplePrices:
        content = (
          <SingleItemMultiplePrices
            purchaseStore={new PurchaseStore()}
            onPurchaseClick={() => {}}
            recommendedItem={customerFacingCheckOutDetails?.recommendedItem}
            items={customerFacingCheckOutDetails?.getSortedItems()}
            color={colors}
            buttonCaption={getButtonCaption()}
            buttonStyle={getButtonStyle()}
          />
        );
        break;
      case CheckOutDisplayTypes.MultipleItems:
        content = (
          <MultipleItems
            purchaseStore={new PurchaseStore()}
            items={customerFacingCheckOutDetails?.getSortedItems()}
            recommendedItem={customerFacingCheckOutDetails?.recommendedItem}
            onPurchaseClick={() => {}}
            groupByBillingFrequency={
              customerFacingCheckOutDetails?.groupByBillingFrequency
            }
            colors={colors}
            buttonCaption={getButtonCaption()}
            buttonStyle={getButtonStyle()}
          />
        );
        break;
      default:
        content = null;
        break;
    }
    return content;
  };

  const getDonationOptions = () => {
    if (!customerFacingCheckOutDetails) return null;
    return (
      <DonationItem
        customerFacingCheckOutDetails={customerFacingCheckOutDetails}
        updateDonationPricingPageData={() => {}}
        onDonationClick={() => {}}
      />
    );
  };

  const getContent = (activeStep: UpsertCheckoutActiveStep) => {
    switch (activeStep) {
      case UpsertCheckoutActiveStep.Sell:
        return getSellPage();
      case UpsertCheckoutActiveStep.Options:
        return getDonationOptions();
      default:
        return null;
    }
  };

  const getPreviewContent = () => {
    if (!customerFacingCheckOutDetails) return null;
    const colors = getColors();
    const hasLogo =
      customerFacingCheckOutDetails?.options.brandingData?.showLogo;
    const hasHelp =
      customerFacingCheckOutDetails?.options.brandingData?.showHelp;
    const hasLogoOrHelp = hasLogo || hasHelp;
    const hasDonationOnly = customerFacingCheckOutDetails?.isDonationOnly;
    return (
      <PurchaseWrapper
        bgColor={colors?.pageBackground?.rgba}
        className={hasLogoOrHelp ? "" : "nav-flex-no-height"}
      >
        <div className="full-width">
          <PurchaseTopBar
            purchaseStore={new PurchaseStore()}
            onTabChange={() => {}}
            showLogo={hasLogo}
            showHelp={hasHelp}
            brandingData={customerFacingCheckOutDetails?.options?.brandingData}
            activeStep={UpsertPurchaseActiveStep.Purchase}
            seller={customerFacingCheckOutDetails?.seller}
          />
        </div>
        {/* <CheckoutPreviewSection> */}

        <div className="purchase-middle-panel">
          <PurchaseColoredBackground
            outerColor={colors?.outerBackground?.rgba}
            outerBorder={colors?.outerBorder?.rgba}
            innerColor={colors?.innerBackground?.rgba}
            innerBorder={colors?.innerBorder?.rgba}
          >
            <div
              className={`purchase-section `}
              // style={{
              //     background: colors?.innerBackground?.rgba,
              //     borderColor: colors?.innerBorder?.rgba,
              // }}
            >
              {getContent(
                hasDonationOnly
                  ? UpsertCheckoutActiveStep.Options
                  : UpsertCheckoutActiveStep.Sell
              )}
            </div>
          </PurchaseColoredBackground>
        </div>
        {/* Commented for now */}
        {/* <div>
          <FlexContainer justifyContent="center" alignItems="center" size={0}>
            <CustomLabel className="purchase-footer">
              Powered by MindMe Payments | Terms | Privacy
            </CustomLabel>
          </FlexContainer>
        </div> */}
        {/* </CheckoutPreviewSection> */}
      </PurchaseWrapper>
    );
  };

  if (loading) return <>{new Loader(true).loaderComponent}</>;
  const hasDonationOnly = customerFacingCheckOutDetails?.isDonationOnly;
  return (
    <DetailPagePreviewWrapper
      className={`preview-wrappper detail-page-preview ${hasDonationOnly ? "donation-only" : ""}`}
    >
      {customerFacingCheckOutDetails && !loading
        ? getPreviewContent()
        : new Loader(true).loaderComponent}
    </DetailPagePreviewWrapper>
  );
};

export default observer(CheckoutPreviewForDetailPage);
